/**
 * Convert an arbitrary value into a `Date` instance if possible.
 * Returns `null` for falsy inputs or invalid dates.
 */
import { parse } from "date-fns";

export function toDate(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : new Date(time);
  }
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const str = value.trim();
    // Common ISO variant with 'T'
    // new Date handles 'YYYY-MM-DDTHH:mm:ss.sssZ' reliably
    const isoCandidate = new Date(str);
    if (!Number.isNaN(isoCandidate.getTime())) return isoCandidate;

    // Handle 'YYYY-MM-DD HH:mm:ss' (no 'T')
    const matchDateTimeSpace = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?$/;
    if (matchDateTimeSpace.test(str)) {
      const pattern = str.includes("T") ? "yyyy-MM-dd'T'HH:mm:ss" : "yyyy-MM-dd HH:mm:ss";
      const d = parse(str, pattern, new Date());
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Handle 'YYYY-MM-DD' only
    const matchDateOnly = /^\d{4}-\d{2}-\d{2}$/;
    if (matchDateOnly.test(str)) {
      const d = parse(str, "yyyy-MM-dd", new Date());
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Handle 'DD.MM.YYYY' and optional time
    const matchGermanDateTime =
      /^\d{2}\.\d{2}\.\d{4}(?:\s+\d{2}:\d{2}(?::\d{2})?)?$/;
    if (matchGermanDateTime.test(str)) {
      const hasSeconds = /^\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2}:\d{2}$/.test(
        str
      );
      const pattern = str.includes(":")
        ? hasSeconds
          ? "dd.MM.yyyy HH:mm:ss"
          : "dd.MM.yyyy HH:mm"
        : "dd.MM.yyyy";
      const d = parse(str, pattern, new Date());
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // Fallback attempt
    const fallback = new Date(str);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  // Final fallback – unsupported type
  return null;
}

/**
 * Normalise any date-like input to local midnight of its calendar day.
 * Returns `null` for unsupported/invalid inputs.
 */
export function normalizeDateOnly(value: unknown): Date | null {
  const date = toDate(value);
  if (!date) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Normalise a date-like input to midnight and return an ISO string.
 * Returns `null` if the input cannot be parsed as a date.
 */
export function normalizeDateOnlyToIso(value: unknown): string | null {
  const normalized = normalizeDateOnly(value);
  return normalized ? normalized.toISOString() : null;
}

/**
 * Convert an exclusive end date (i.e. following day midnight) to an inclusive ISO date string.
 * Returns `null` for invalid inputs.
 */
export function normalizeExclusiveEndToIso(value: unknown): string | null {
  const exclusive = normalizeDateOnly(value);
  if (!exclusive) return null;
  const inclusive = new Date(exclusive);
  inclusive.setDate(inclusive.getDate() - 1);
  return inclusive.toISOString();
}
